import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtTL } from "@/components/StatusBadge";
import { ShieldCheck, MessageSquare, Loader2 } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

function Kpi({ label, value, accent }) {
  return (
    <div className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200">
      <p className="overline">{label}</p>
      <p className={`tabular mt-1 text-xl font-black ${accent || "text-slate-800"}`}>{value}</p>
    </div>
  );
}

const MethodBadge = ({ m }) => (
  m === "sms" ? <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700"><MessageSquare className="h-3 w-3" /> SMS</span>
  : m === "supervisor" ? <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 text-[11px] font-semibold text-brand-orange"><ShieldCheck className="h-3 w-3" /> Amir onayı</span>
  : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">—</span>
);

export default function WholesaleAudit() {
  const [start, setStart] = useState(daysAgoISO(30));
  const [end, setEnd] = useState(todayISO());
  const [method, setMethod] = useState("");
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    if (method) p.set("method", method);
    api.get(`/sales/wholesale-audit?${p}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [start, end, method]);

  const s = data.summary || {};
  return (
    <div className="space-y-4" data-testid="wholesale-audit-page">
      <div>
        <h1 className="font-heading text-2xl font-black text-slate-800">Toptan Yetki Denetimi</h1>
        <p className="text-sm text-slate-500">Her toptan (fırsatlı) satışın nasıl yetkilendirildiği — müşteri SMS'i mi, amir onayı mı ve kim onayladı.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Toptan Satış" value={s.total ?? 0} />
        <Kpi label="Toplam Tutar" value={fmtTL(s.amount || 0)} accent="text-brand-green" />
        <Kpi label="SMS ile" value={s.sms ?? 0} accent="text-sky-700" />
        <Kpi label="Amir onayı" value={s.supervisor ?? 0} accent="text-brand-orange" />
      </div>
      <div className="flex flex-wrap items-end gap-3 rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200">
        <div><label className="overline">Başlangıç</label><DatePickerTR testid="wa-start" value={start} onChange={(v) => setStart(v)} className="mt-1 block h-9 w-40" /></div>
        <div><label className="overline">Bitiş</label><DatePickerTR testid="wa-end" value={end} onChange={(v) => setEnd(v)} className="mt-1 block h-9 w-40" /></div>
        <div><label className="overline">Yöntem</label>
          <select data-testid="wa-method" value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 block h-9 rounded-md border border-input bg-white px-2 text-sm">
            <option value="">Tümü</option><option value="sms">SMS</option><option value="supervisor">Amir onayı</option>
          </select>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400">
            <th className="p-3">Tarih</th><th className="p-3">Fiş No</th><th className="p-3">Müşteri</th><th className="p-3">Kasiyer</th><th className="p-3">Yetki</th><th className="p-3">Onaylayan</th><th className="p-3 text-right">Tutar</th>
          </tr></thead>
          <tbody data-testid="wa-list">
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
            {!loading && (data.items || []).length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Bu aralıkta toptan satış yok</td></tr>}
            {!loading && (data.items || []).map((r) => (
              <tr key={r.id} data-testid={`wa-row-${r.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="p-3 text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                <td className="p-3 font-mono text-xs">{r.sale_no}</td>
                <td className="p-3 font-medium text-slate-700">{r.customer_name || "—"}</td>
                <td className="p-3 text-slate-500">{r.cashier}</td>
                <td className="p-3"><MethodBadge m={r.method} /></td>
                <td className="p-3 text-slate-600">{r.approver || (r.method === "sms" ? "Müşteri (SMS)" : "—")}</td>
                <td className="p-3 text-right tabular font-bold text-slate-800">{fmtTL(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
